export type LandingLinks = {
  appUrl: string;
  webAppUrl: string;
  discordUrl: string;
  docsUrl: string;
  githubUrl: string;
  /** The docs folder in the GitHub repository (en at docs/, zh at docs/zh/). */
  docsSourceUrl: string;
  protocolUrl: string;
  downloadUrl: string;
  desktopDownloadUrl: string;
  appsUrl: string;
  worldsUrl: string;
  createAppUrl: string;
  createGuideUrl: string;
  modelsUrl: string;
  riskControlUrl: string;
};

// Docs site is deployed at the docs.nimi.ai subdomain (VitePress with
// cleanUrls + locales: en at root, zh under /zh/). Locale prefixing for zh
// is applied in App.tsx via resolveLocalizedLinks(); these defaults are the
// en/root URLs.
const DEFAULT_LINKS: LandingLinks = {
  appUrl: 'https://docs.nimi.ai/start/',
  webAppUrl: '/home',
  discordUrl: 'https://discord.gg/BQwHJvPn',
  docsUrl: 'https://docs.nimi.ai/',
  githubUrl: 'https://github.com/nimiplatform/nimi',
  docsSourceUrl: 'https://github.com/nimiplatform/nimi/tree/main/docs',
  protocolUrl: 'https://docs.nimi.ai/platform/protocol',
  downloadUrl: '/download',
  desktopDownloadUrl: 'https://docs.nimi.ai/desktop/',
  appsUrl: '/apps',
  worldsUrl: '/home',
  createAppUrl: '/apps#create-your-own',
  createGuideUrl: 'https://docs.nimi.ai/start/create-an-app',
  modelsUrl: 'https://docs.nimi.ai/runtime/connectors-and-providers',
  riskControlUrl: 'https://docs.nimi.ai/runtime/ai-risk-control',
};

/**
 * Insert a locale prefix (e.g. 'zh') into a docs URL on the docs.nimi.ai
 * subdomain. en/root locale returns the URL unchanged; zh transforms
 * `https://docs.nimi.ai/<path>` → `https://docs.nimi.ai/zh/<path>`.
 *
 * Non-docs URLs (webAppUrl, discordUrl, githubUrl) pass through unchanged.
 */
function localizeDocsUrl(url: string, locale: 'en' | 'zh'): string {
  if (locale === 'en') return url;
  try {
    const parsed = new URL(url);
    if (parsed.host !== 'docs.nimi.ai') return url;
    if (parsed.pathname.startsWith('/zh/') || parsed.pathname === '/zh') return url;
    parsed.pathname = '/zh' + parsed.pathname;
    return parsed.toString();
  } catch {
    // Relative path fallback (only used if env override deviates from default).
    if (url.startsWith('/docs/zh/')) return url;
    if (url.startsWith('/docs/')) return '/docs/zh/' + url.slice('/docs/'.length);
    return url;
  }
}

/**
 * Point the GitHub docs folder link at the locale's subfolder: en stays at
 * `docs`, zh becomes `docs/zh`. Any other URL passes through unchanged.
 */
function localizeDocsSourceUrl(url: string, locale: 'en' | 'zh'): string {
  if (locale === 'en') return url;
  if (url.endsWith('/docs/zh') || url.endsWith('/docs/zh/')) return url;
  if (url.endsWith('/docs')) return `${url}/zh`;
  if (url.endsWith('/docs/')) return `${url}zh`;
  return url;
}

/**
 * Append the active locale to a same-origin site URL so receiving pages keep
 * the language named by the current URL. Existing query parameters and the
 * hash are preserved.
 */
export function withLocaleQuery(href: string, locale: 'en' | 'zh'): string {
  const hashIndex = href.indexOf('#');
  const beforeHash = hashIndex >= 0 ? href.slice(0, hashIndex) : href;
  const hash = hashIndex >= 0 ? href.slice(hashIndex + 1) : null;
  const queryIndex = beforeHash.indexOf('?');
  const path = queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;
  const query = queryIndex >= 0 ? beforeHash.slice(queryIndex + 1) : '';
  const params = new URLSearchParams(query);
  params.set('lang', locale);
  const search = params.toString();
  return `${path}${search ? `?${search}` : ''}${hash !== null ? `#${hash}` : ''}`;
}

/**
 * Build an app detail URL from the localized apps base (`/apps?lang=zh`),
 * keeping the query ahead of the path segment.
 */
export function buildAppDetailUrl(appsBase: string, appId: string): string {
  const hashIndex = appsBase.indexOf('#');
  const beforeHash = hashIndex >= 0 ? appsBase.slice(0, hashIndex) : appsBase;
  const hash = hashIndex >= 0 ? appsBase.slice(hashIndex + 1) : null;
  const queryIndex = beforeHash.indexOf('?');
  const path = queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;
  const query = queryIndex >= 0 ? beforeHash.slice(queryIndex + 1) : '';
  return `${path}/${appId}${query ? `?${query}` : ''}${hash !== null ? `#${hash}` : ''}`;
}

/**
 * Apply locale prefix to docs URLs and locale parameter to internal site
 * URLs. External non-docs URLs pass through unchanged.
 */
export function resolveLocalizedLinks(links: LandingLinks, locale: 'en' | 'zh'): LandingLinks {
  return {
    ...links,
    appUrl: localizeDocsUrl(links.appUrl, locale),
    docsUrl: localizeDocsUrl(links.docsUrl, locale),
    docsSourceUrl: localizeDocsSourceUrl(links.docsSourceUrl, locale),
    protocolUrl: localizeDocsUrl(links.protocolUrl, locale),
    desktopDownloadUrl: localizeDocsUrl(links.desktopDownloadUrl, locale),
    createGuideUrl: localizeDocsUrl(links.createGuideUrl, locale),
    modelsUrl: localizeDocsUrl(links.modelsUrl, locale),
    riskControlUrl: localizeDocsUrl(links.riskControlUrl, locale),
    downloadUrl: withLocaleQuery(links.downloadUrl, locale),
    appsUrl: withLocaleQuery(links.appsUrl, locale),
    worldsUrl: withLocaleQuery(links.worldsUrl, locale),
    createAppUrl: withLocaleQuery(links.createAppUrl, locale),
  };
}

function normalizeUrl(raw: unknown, fallback: string): string {
  const value = typeof raw === 'string' ? raw.trim() : '';
  if (!value) return fallback;
  try {
    const relativeBase = 'https://web.nimi.invalid';
    const parsed = new URL(value, relativeBase);
    if (parsed.hash) return fallback;
    if (value.startsWith('/') && !value.startsWith('//') && parsed.origin === relativeBase) {
      return `${parsed.pathname}${parsed.search}`;
    }
    return (parsed.protocol === 'http:' || parsed.protocol === 'https:') && parsed.origin !== relativeBase
      ? parsed.toString()
      : fallback;
  } catch {
    return fallback;
  }
}

export function resolveLandingLinks(env: Record<string, unknown> = {}): LandingLinks {
  return {
    appUrl: normalizeUrl(env.VITE_LANDING_APP_URL, DEFAULT_LINKS.appUrl),
    webAppUrl: normalizeUrl(env.VITE_LANDING_WEB_APP_URL, DEFAULT_LINKS.webAppUrl),
    discordUrl: normalizeUrl(env.VITE_LANDING_DISCORD_URL, DEFAULT_LINKS.discordUrl),
    docsUrl: normalizeUrl(env.VITE_LANDING_DOCS_URL, DEFAULT_LINKS.docsUrl),
    githubUrl: normalizeUrl(env.VITE_LANDING_GITHUB_URL, DEFAULT_LINKS.githubUrl),
    docsSourceUrl: normalizeUrl(env.VITE_LANDING_DOCS_SOURCE_URL, DEFAULT_LINKS.docsSourceUrl),
    protocolUrl: normalizeUrl(env.VITE_LANDING_PROTOCOL_URL, DEFAULT_LINKS.protocolUrl),
    downloadUrl: DEFAULT_LINKS.downloadUrl,
    desktopDownloadUrl: normalizeUrl(
      env.VITE_LANDING_DESKTOP_DOWNLOAD_URL,
      DEFAULT_LINKS.desktopDownloadUrl,
    ),
    appsUrl: DEFAULT_LINKS.appsUrl,
    worldsUrl: DEFAULT_LINKS.worldsUrl,
    createAppUrl: DEFAULT_LINKS.createAppUrl,
    createGuideUrl: DEFAULT_LINKS.createGuideUrl,
    modelsUrl: normalizeUrl(env.VITE_LANDING_MODELS_URL, DEFAULT_LINKS.modelsUrl),
    riskControlUrl: DEFAULT_LINKS.riskControlUrl,
  };
}

export const landingLinkDefaults = DEFAULT_LINKS;
